import React, { useState, useEffect, useCallback } from 'react';
import { Gate } from '../AccessStudio';

// Class-level daily report: the shared "What I learned today" activity checklist
// broadcast to the whole class. (Audio is Phase A2.) See DAYCARE_DIARY_DESIGN.md.

const DaycareClassReportModal = ({ isOpen, onClose, onSuccess, classId, className, date, apiCall, showError, showSuccess }) => {
  const [activities, setActivities] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!classId || !date) return;
    setLoading(true);
    try {
      const [acts, rep] = await Promise.all([
        apiCall('/daycare/daily-report/activities'),
        apiCall(`/daycare/daily-report/class?classId=${classId}&date=${date}`),
      ]);
      setActivities(acts || []);
      setSelected(rep.activityIds || []);
    } catch (e) {
      showError('Error loading class report: ' + e.message);
    } finally { setLoading(false); }
  }, [classId, date, apiCall, showError]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const save = async () => {
    setSaving(true);
    try {
      await apiCall('/daycare/daily-report/class', {
        method: 'PUT', body: JSON.stringify({ classId, date, activityIds: selected }),
      });
      showSuccess('Class report saved');
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      showError('Error saving class report: ' + e.message);
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Class Report — {className}</h2>
            <p className="text-indigo-100 text-sm">What I learned today · {date}</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl leading-none">&times;</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : (
          <div className="p-6 overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {activities.map((a) => (
                <label key={a.ActivityID} className="flex items-center gap-2 text-sm text-gray-700 py-0.5">
                  <input type="checkbox" checked={selected.includes(a.ActivityID)} onChange={() => toggle(a.ActivityID)} />
                  {a.Label}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <Gate permission="daycare.daily_report.edit" label="Save class report">
            <button onClick={save} disabled={saving || loading} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-300">{saving ? 'Saving…' : 'Save'}</button>
          </Gate>
        </div>
      </div>
    </div>
  );
};

export default DaycareClassReportModal;
